import { useEffect, useState } from 'react';
import { Plus, ArrowUp, ArrowDown } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import {
  adminList, adminCreate, adminUpdate, adminDelete, adminReorder,
} from '../../../lib/adminApi';
import { SectionHeader, SaveBar, useSaver, ConfirmDelete, Field, Card } from './_ui';
import ImagePicker from './ImagePicker';

function toLines(arr) { return (arr || []).join('\n'); }
function fromLines(str) {
  return String(str || '').split('\n').map((s) => s.trim()).filter(Boolean);
}

export default function TeamSection() {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => { setTutors(await adminList('tutors')); setLoading(false); };
  useEffect(() => { refresh(); }, []);

  const addTutor = async () => {
    await adminCreate('tutors', {
      name: 'New tutor', role: '', bio: '', bioExtra: '',
      credentials: [], subjects: [],
      image: '', photoVisible: true, active: true, order: tutors.length,
    });
    await refresh();
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= tutors.length) return;
    const next = [...tutors];
    [next[i], next[j]] = [next[j], next[i]];
    setTutors(next);
    await adminReorder('tutors', next.map((t) => t.id));
  };

  return (
    <div className="max-w-5xl">
      <SectionHeader
        title="Team"
        description="Tutor cards. Toggle photo visibility or active status without deleting."
        actions={
          <Button onClick={addTutor} className="bg-brand-sun text-brand-ink hover:brightness-95 rounded-full">
            <Plus className="w-4 h-4 mr-1" /> Add tutor
          </Button>
        }
      />
      {loading ? <p className="text-brand-ink/60">Loading...</p> : (
        <div className="space-y-4">
          {tutors.map((t, i) => (
            <TutorRow
              key={t.id}
              tutor={t}
              index={i}
              total={tutors.length}
              onChange={(next) =>
                setTutors((prev) => prev.map((x) => (x.id === t.id ? { ...x, ...next } : x)))
              }
              onSave={async (next) => { await adminUpdate('tutors', t.id, next); }}
              onDelete={async () => { await adminDelete('tutors', t.id); await refresh(); }}
              onMoveUp={() => move(i, -1)}
              onMoveDown={() => move(i, 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TutorRow({ tutor, index, total, onChange, onSave, onDelete, onMoveUp, onMoveDown }) {
  const saver = useSaver();
  const t = tutor;
  return (
    <Card>
      <div className="flex items-start gap-3">
        <div className="flex flex-col shrink-0">
          <button type="button" onClick={onMoveUp} disabled={index === 0}
                  className="p-1 rounded-md hover:bg-brand-ink/5 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
          <button type="button" onClick={onMoveDown} disabled={index === total - 1}
                  className="p-1 rounded-md hover:bg-brand-ink/5 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 min-w-0 grid md:grid-cols-2 gap-3">
          <Field label="Name">
            <Input value={t.name || ''} onChange={(e) => onChange({ name: e.target.value })} />
          </Field>
          <Field label="Role">
            <Input value={t.role || ''} onChange={(e) => onChange({ role: e.target.value })} />
          </Field>
          <Field label="Biography" cols={2}>
            <Textarea rows={3} value={t.bio || ''} onChange={(e) => onChange({ bio: e.target.value })} />
          </Field>
          <Field label="Extra bio paragraph" cols={2}>
            <Textarea rows={2} value={t.bioExtra || ''} onChange={(e) => onChange({ bioExtra: e.target.value })} />
          </Field>
          <Field label="Qualifications & examining credentials (one per line)" cols={2}>
            <Textarea
              rows={4}
              value={toLines(t.credentials)}
              onChange={(e) => onChange({ credentials: fromLines(e.target.value) })}
            />
          </Field>
          <Field label="Subjects (comma separated)" cols={2}>
            <Input
              value={(t.subjects || []).join(', ')}
              onChange={(e) => onChange({
                subjects: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
              })}
            />
          </Field>
          <Field label="Photo URL" cols={2}>
            <ImagePicker
              label=""
              value={t.image}
              onChange={(url) => onChange({ image: url })}
              size="lg"
            />
          </Field>

          <div className="md:col-span-2 flex items-center gap-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <Switch checked={t.photoVisible !== false} onCheckedChange={(v) => onChange({ photoVisible: v })} />
              Show photo
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <Switch checked={t.active !== false} onCheckedChange={(v) => onChange({ active: v })} />
              Active
            </label>
          </div>

          <div className="md:col-span-2 flex items-center justify-between pt-2 border-t border-brand-ink/10 mt-2">
            <ConfirmDelete onConfirm={onDelete} />
            <SaveBar {...saver} onSave={() => saver.run(() => onSave(t))} dataTestId={`save-tutor-${index}`} />
          </div>
        </div>
      </div>
    </Card>
  );
}
